
import React, { useEffect, useState, useRef } from 'react';
import { X, Zap, Heart, Type, MessageSquare, Link as LinkIcon, Palette, ExternalLink, Move, Maximize, RotateCcw, Sparkles, Ban, Paintbrush, Layout, Loader2, Pipette, AlertCircle, LayoutGrid, CreditCard, ChevronLeft, Share2, Check } from 'lucide-react';
import { ArtItem, Language, Currency, ArtCategory } from '../types';
import { MASTER_FALLBACK_IMAGE } from '../constants';
import { formatCurrency } from '../utils';

const categoryLabels: Record<string, { pt: string; en: string; es?: string; ru?: string }> = {
  [ArtCategory.ANIMATED || 'Animated']: { pt: 'Animado', en: 'Animated', es: 'Animado', ru: 'Анимированный' },
  [ArtCategory.STATIC || 'Static']: { pt: 'Estático', en: 'Static', es: 'Estático', ru: 'Статичный' },
  [ArtCategory.BLACK_AND_WHITE || 'Black and White']: { pt: 'Preto & Branco', en: 'Black & White', es: 'Blanco y Negro', ru: 'Черно-белый' },
  [ArtCategory.WORKSHOP || 'Workshop']: { pt: 'Destaque / Workshop', en: 'Workshop Showcase', es: 'Expositor Workshop', ru: 'Витрина Мастерской' },
  [ArtCategory.AVATAR || 'Avatar']: { pt: 'Avatar', en: 'Avatar', es: 'Avatar', ru: 'Аватар' },
  [ArtCategory.CLIENT_ORDERS || 'Client Orders']: { pt: 'Pedidos de Clientes', en: 'Client Orders', es: 'Pedidos de Clientes', ru: 'Заказы Клиентов' },
  [ArtCategory.DISCORD_BANNER || 'Discord Banner']: { pt: 'Banner para Discord', en: 'Discord Banner', es: 'Banner de Discord', ru: 'Баннер Discord' },
  [ArtCategory.WALLPAPER || 'Wallpaper / Background']: { pt: 'Fundo / Wallpaper', en: 'Wallpaper / Background', es: 'Fondo / Wallpaper', ru: 'Фон / Обои' },
};

const presetColors = ['#ffffff', '#000000', '#9333ea', '#ff4d4d', '#4dff4d', '#4d4dff', '#ffff4d', '#ff4dff', '#4dffff'];

interface ImageModalProps {
  item: ArtItem;
  onClose: () => void;
  onAddToCart: (item: ArtItem, signature?: string, discord?: string, steamUrl?: string, font?: string, dafontLink?: string, sigPosX?: number, sigPosY?: number, sigScale?: number, sigColor?: string, notes?: string) => void;
  language: Language;
  currency?: Currency;
  isPage?: boolean;
  discountPercentage?: number;
}

const ImageModal: React.FC<ImageModalProps> = ({ item, onClose, onAddToCart, language, currency = 'USD', isPage = false, discountPercentage = 0 }) => {
  const hasDiscount = discountPercentage > 0;
  const finalPrice = hasDiscount ? item.price * (1 - discountPercentage / 100) : item.price;
  const itemWithDiscount = hasDiscount ? { ...item, price: finalPrice } : item;
  const [hasError, setHasError] = useState(false);
  const [mediaReady, setMediaReady] = useState(false);
  const [copied, setCopied] = useState(false);
  
  const [customName, setCustomName] = useState('');
  const [discord, setDiscord] = useState('');
  const [steamUrl, setSteamUrl] = useState('');
  const [dafontLink, setDafontLink] = useState('');
  const [notes, setNotes] = useState('');
  
  const [posX, setPosX] = useState(0); 
  const [posY, setPosY] = useState(60); 
  const [scale, setScale] = useState(1.0);
  const [sigColor, setSigColor] = useState('#ffffff');
  
  const colorInputRef = useRef<HTMLInputElement>(null);

  const handleCopyLink = (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    const origin = window.location.origin.includes('localhost') || window.location.origin.includes('run.app')
      ? window.location.origin
      : 'https://steamniikoartwork.shop';
    const shareUrl = `${origin}/art/${item.id}`;
    
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(shareUrl).then(() => {
        setCopied(true);
        setTimeout(() => setCopied(false), 2500);
      }).catch(() => fallbackCopy(shareUrl));
    } else {
      fallbackCopy(shareUrl);
    }
  };

  const fallbackCopy = (text: string) => {
    try {
      const input = document.createElement('input');
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      console.warn('Copy failed', e);
    }
  };

  const t = {
    pt: { 
      live: 'Obra Viva', static: 'Estático', buy: 'Adquirir Arte', personalize: 'Assinatura Personalizada',
      dafontLabel: 'Link da Fonte (Dafont)', namePlaceholder: 'Seu nome aqui...', dafontPlaceholder: 'https://www.dafont.com/pt/font.php?...',
      discord: 'Discord (para entrega)', discordPlaceholder: 'Ex: usuario#0000', steam: 'Link do Perfil Steam', steamPlaceholder: 'https://steamcommunity.com/id/...',
      adjustments: 'Ajuste do Selo', posX: 'Posição X', posY: 'Posição Y', scale: 'Tamanho', color: 'Cor da Assinatura',
      customColor: 'Cor Personalizada', reset: 'Resetar', nfs: 'Obra Indisponível para Venda',
      suggestedBg: 'Fundo Sugerido (Steam)', viewOnMarket: 'Ver no Mercado', error: 'Erro ao Carregar Mídia',
      notesLabel: 'Observações / Instruções Adicionais', notesPlaceholder: 'Ex: Gostaria de tal efeito especial, detalhes sobre a arte, outros links ou especificações...',
      share: 'Compartilhar Link', linkCopied: 'Link Copiado!'
    },
    en: { 
      live: 'Live Artwork', static: 'Static Art', buy: 'Aquire Artwork', personalize: 'Custom Signature',
      dafontLabel: 'Font Link (Dafont)', namePlaceholder: 'Your name here...', dafontPlaceholder: 'https://www.dafont.com/font.php?...',
      discord: 'Discord (for delivery)', discordPlaceholder: 'Ex: user#0000', steam: 'Steam Profile Link', steamPlaceholder: 'https://steamcommunity.com/id/...',
      adjustments: 'Seal Adjustments', posX: 'X Position', posY: 'Y Position', scale: 'Scale', color: 'Signature Color',
      customColor: 'Custom Color', reset: 'Reset', nfs: 'Artwork Not for Sale',
      suggestedBg: 'Suggested Background (Steam)', viewOnMarket: 'View on Market', error: 'Media Loading Error',
      notesLabel: 'Observation / Additional Notes', notesPlaceholder: 'e.g., Special visual adjustments, custom elements, or any notes you want to add...',
      share: 'Share Link', linkCopied: 'Link Copied!'
    },
    es: { 
      live: 'Obra Viva', static: 'Arte Estático', buy: 'Adquirir Obra', personalize: 'Firma Personalizada',
      dafontLabel: 'Enlace de Fuente (Dafont)', namePlaceholder: 'Tu nombre aquí...', dafontPlaceholder: 'https://www.dafont.com/es/font.php?...',
      discord: 'Discord (para entrega)', discordPlaceholder: 'Ej: usuario#0000', steam: 'Enlace de Perfil de Steam', steamPlaceholder: 'https://steamcommunity.com/id/...',
      adjustments: 'Ajustes del Sello', posX: 'Posición X', posY: 'Posición Y', scale: 'Tamaño', color: 'Color de Firma',
      customColor: 'Color Personalizado', reset: 'Reiniciar', nfs: 'Obra No Disponible para Venta',
      suggestedBg: 'Fondo Sugerido (Steam)', viewOnMarket: 'Ver en Mercado', error: 'Error al Cargar Medio',
      notesLabel: 'Observaciones / Notas Adicionales', notesPlaceholder: 'Ej: Efectos especiales, detalles, otros enlaces...',
      share: 'Compartir Enlace', linkCopied: '¡Enlace Copiado!'
    },
    ru: { 
      live: 'Живой Арт', static: 'Статичный Арт', buy: 'Купить Арт', personalize: 'Персональная Подпись',
      dafontLabel: 'Ссылка на Шрифт (Dafont)', namePlaceholder: 'Ваш ник здесь...', dafontPlaceholder: 'https://www.dafont.com/...',
      discord: 'Discord (для доставки)', discordPlaceholder: 'Например: user#0000', steam: 'Ссылка на профиль Steam', steamPlaceholder: 'https://steamcommunity.com/id/...',
      adjustments: 'Настройки Подписи', posX: 'Позиция X', posY: 'Позиция Y', scale: 'Масштаб', color: 'Цвет Подписи',
      customColor: 'Свой Цвет', reset: 'Сброс', nfs: 'Работа не продается',
      suggestedBg: 'Рекомендуемый Фон (Steam)', viewOnMarket: 'В Магазине Steam', error: 'Ошибка загрузки медиа',
      notesLabel: 'Примечания / Пожелания', notesPlaceholder: 'Например: Особые визуальные эффекты, детали...',
      share: 'Поделиться Ссылкой', linkCopied: 'Ссылка Скопирована!'
    }
  }[language] || { 
    live: 'Live Artwork', static: 'Static Art', buy: 'Aquire Artwork', personalize: 'Custom Signature',
    dafontLabel: 'Font Link (Dafont)', namePlaceholder: 'Your name here...', dafontPlaceholder: 'https://www.dafont.com/font.php?...',
    discord: 'Discord (for delivery)', discordPlaceholder: 'Ex: user#0000', steam: 'Steam Profile Link', steamPlaceholder: 'https://steamcommunity.com/id/...',
    adjustments: 'Seal Adjustments', posX: 'X Position', posY: 'Y Position', scale: 'Scale', color: 'Signature Color',
    customColor: 'Custom Color', reset: 'Reset', nfs: 'Artwork Not for Sale',
    suggestedBg: 'Suggested Background (Steam)', viewOnMarket: 'View on Market', error: 'Media Loading Error',
    notesLabel: 'Observation / Additional Notes', notesPlaceholder: 'e.g., Special visual adjustments, custom elements, or any notes you want to add...',
    share: 'Share Link', linkCopied: 'Link Copied!'
  };

  const [videoFailed, setVideoFailed] = useState(false);

  useEffect(() => {
    setHasError(false);
    setVideoFailed(false);
    setMediaReady(false);
  }, [item.id, item.videoUrl, item.imageUrl]);

  const handleReset = () => {
    setPosX(0);
    setPosY(60);
    setScale(1.0);
    setSigColor('#ffffff');
  };

  const formatPrice = (price: number) => {
    if (item.notForSale) return t.nfs;
    return formatCurrency(price, currency);
  };

  // Sanitiza URLs para prevenir caminhos quebrados como /art/uploads/
  const sanitizeUrl = (url?: string) => {
    if (!url) return '';
    return url.replace(/\/art(?:\/[^/]+)?\/uploads\//g, '/uploads/');
  };

  const videoSource = sanitizeUrl(item.videoUrl || '');
  const imageSource = sanitizeUrl(item.imageUrl || MASTER_FALLBACK_IMAGE);

  const isVideo = !videoFailed && (
    videoSource.toLowerCase().endsWith('.mp4') || 
    videoSource.includes('giphy.com') || 
    videoSource.startsWith('blob:')
  );

  return (
    <div className={isPage ? "w-full flex flex-col animate-in fade-in duration-500" : "fixed inset-0 z-[150] flex items-center justify-center p-4 sm:p-6 md:p-8 lg:p-12 animate-in fade-in duration-300"}>
      {!isPage && <div className="absolute inset-0 bg-black/85 backdrop-blur-md" onClick={onClose} />}
      
      {isPage && (
        <button 
          onClick={onClose} 
          className="flex items-center gap-3 text-zinc-400 hover:text-[#c084fc] mb-8 text-[10px] font-black uppercase tracking-widest transition-all self-start"
        >
          <ChevronLeft className="w-4 h-4" /> {language === 'en' ? 'Back to Gallery' : 'Voltar para a Galeria'}
        </button>
      )}

      <style>{`
        @keyframes border-glow-spin {
          0% {
            transform: translate(-50%, -50%) rotate(0deg);
          }
          100% {
            transform: translate(-50%, -50%) rotate(360deg);
          }
        }
        .my-spinning-border {
          width: 150vmax;
          height: 150vmax;
          background: conic-gradient(
            from 0deg,
            #a855f7 0%,
            #ec4899 10%,
            #fdf2f8 22%,
            #ffffff 30%,
            #ffffff 45%,
            #fdf2f8 50%,
            #d946ef 60%,
            #f472b6 72%,
            #ffffff 80%,
            #ffffff 90%,
            #fdf2f8 95%,
            #a855f7 100%
          );
          animation: border-glow-spin 5s linear infinite !important;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(0deg);
          will-change: transform;
        }
      `}</style>

      <div 
        className={isPage 
          ? "relative w-full overflow-hidden shadow-2xl select-none p-[2.5px] bg-transparent" 
          : "relative w-full max-w-5xl h-auto max-h-[92vh] md:max-h-[88vh] overflow-hidden shadow-[0_0_55px_rgba(147,51,234,0.75)] select-none rounded-2xl p-[2.5px] bg-transparent animate-in zoom-in-95 duration-300 flex flex-col"
        }
        onContextMenu={(e) => e.preventDefault()}
      >
        {/* Rotating violet glow loop border */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none z-0 rounded-2xl">
          <div className="my-spinning-border" />
        </div>

        {/* Inner Content Container with glassmorphism and moving gradient */}
        <div className={isPage
          ? "relative w-full h-full bg-[#070114] moving-glass-gradient flex flex-col md:flex-row z-10"
          : "relative w-full h-full max-h-[92vh] md:max-h-[88vh] bg-[#070114] moving-glass-gradient backdrop-blur-xl rounded-[14px] flex flex-col md:flex-row overflow-hidden z-10"
        }>
          {!isPage && (
            <button 
              onClick={onClose} 
              className="absolute top-3 right-3 md:top-4 md:right-4 z-[160] p-2 md:p-2.5 bg-[#12032d]/90 backdrop-blur-md hover:bg-[#9333ea] hover:text-white text-purple-300 transition-all border border-[#9333ea]/30 rounded-full shadow-lg"
              aria-label="Close"
            >
              <X className="w-4 h-4 md:w-5 md:h-5" />
            </button>
          )}

        <div className={`flex-[1.3] md:flex-[1.5] bg-black relative flex items-center justify-center p-2 sm:p-3 md:p-4 overflow-hidden ${isPage ? 'min-h-[50vh] md:min-h-[75vh]' : 'min-h-[35vh] md:min-h-0'}`}>
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 z-0">
             {!hasError && !mediaReady && <Loader2 className="w-8 h-8 text-[#9333ea] animate-spin opacity-40" />}
          </div>

          {hasError ? (
            <div className="flex flex-col items-center justify-center gap-3 text-zinc-400 z-10 text-center px-4 animate-in fade-in">
              <AlertCircle className="w-14 h-14 text-purple-400 opacity-60" />
              <p className="text-xs font-black uppercase tracking-[0.3em] text-zinc-300">{t.error}</p>
              <button
                type="button"
                onClick={() => {
                  setHasError(false);
                  setVideoFailed(false);
                  setMediaReady(false);
                }}
                className="mt-2 px-4 py-1.5 bg-[#9333ea]/30 hover:bg-[#9333ea] border border-[#9333ea]/50 text-white rounded-lg text-[10px] font-black uppercase tracking-wider transition-all"
              >
                {language === 'pt' ? 'Recarregar Mídia' : 'Retry Media'}
              </button>
            </div>
          ) : (
            <div className="w-full h-full flex items-center justify-center relative z-10">
              <div className="relative w-fit h-fit max-w-full max-h-full shadow-2xl flex items-center justify-center bg-black">
                <div 
                  className="absolute inset-0 z-20 bg-transparent cursor-default" 
                  onContextMenu={(e) => e.preventDefault()}
                  onDragStart={(e) => e.preventDefault()}
                />

                {isVideo ? (
                  <video 
                    key={`modal-vid-${item.id}-${videoSource}`}
                    src={videoSource} 
                    autoPlay loop muted playsInline 
                    disablePictureInPicture
                    controlsList="nodownload"
                    preload="auto"
                    poster={imageSource}
                    onCanPlay={() => setMediaReady(true)}
                    onError={() => {
                      console.warn('Video failed in modal, falling back to image preview:', videoSource);
                      setVideoFailed(true);
                      if (!imageSource || imageSource === MASTER_FALLBACK_IMAGE) {
                        setHasError(true);
                      }
                    }}
                    className={isPage ? "max-w-full max-h-[85vh] md:max-h-[90vh] w-auto h-auto object-contain block pointer-events-none" : "max-w-full max-h-[72vh] md:max-h-[80vh] w-auto h-auto object-contain block pointer-events-none"} 
                  />
                ) : (
                  <img 
                    key={`modal-img-${item.id}-${imageSource}`}
                    src={imageSource || MASTER_FALLBACK_IMAGE} 
                    alt={item.title[language] || item.title['en'] || item.title['pt'] || 'Artwork Preview'} 
                    decoding="async"
                    onLoad={() => setMediaReady(true)}
                    onError={() => {
                      if (!imageSource.includes(MASTER_FALLBACK_IMAGE)) {
                        setMediaReady(true);
                      } else {
                        setHasError(true);
                      }
                    }}
                    className={isPage ? "max-w-full max-h-[85vh] md:max-h-[90vh] w-auto h-auto object-contain block pointer-events-none" : "max-w-full max-h-[72vh] md:max-h-[80vh] w-auto h-auto object-contain block pointer-events-none"}
                    draggable="false"
                  />
                )}
              </div>
            </div>
          )}
        </div>

        <div className={`flex-1 w-full md:w-[420px] lg:w-[460px] p-4 sm:p-5 md:p-6 ${isPage ? 'bg-[#0b021d]/98' : 'bg-[#0b021d]/95 md:bg-purple-950/25 backdrop-blur-md'} flex flex-col justify-between border-l border-purple-500/25 shadow-2xl z-30 overflow-hidden`}>
          {/* Header section (Compact) */}
          <div className="mb-2.5 pb-2.5 border-b border-white/5 flex-shrink-0">
            <div className="flex flex-wrap items-center gap-1.5 mb-2 pr-10">
              <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider shadow-sm ${item.isAnimated ? 'bg-[#9333ea] text-white' : 'bg-zinc-800 text-zinc-400'}`}>
                {item.isAnimated ? t.live : t.static}
              </span>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider shadow-sm bg-[#120324] border border-[#9333ea]/20 text-zinc-300">
                <LayoutGrid className="w-2.5 h-2.5 text-[#9333ea]" />
                {categoryLabels[item.category]?.[language] || item.category}
                {item.subcategory && <span className="text-purple-400 font-black ml-1 text-[8px] lowercase opacity-70">/ {item.subcategory}</span>}
              </span>
              {item.artType && (
                <span className="inline-flex items-center gap-1 px-2.5 py-0.5 text-[9px] font-black uppercase tracking-wider shadow-sm bg-black border border-[#9333ea]/30 text-[#c084fc]">
                  <Sparkles className="w-2.5 h-2.5 text-[#9333ea] animate-pulse" />
                  {item.artType}
                </span>
              )}
            </div>

            <div className="flex items-center justify-between gap-3">
              <h2 className="text-lg sm:text-xl md:text-2xl font-black text-white uppercase tracking-tight oil-text leading-tight truncate">
                {item.title[language] || item.title['en'] || item.title['pt'] || ''}
              </h2>
              
              <div className="flex items-baseline gap-2 flex-shrink-0">
                {item.notForSale ? (
                  <p className="text-zinc-500 italic text-xs font-mono font-black">
                    {t.nfs}
                  </p>
                ) : hasDiscount ? (
                  <div className="flex items-baseline gap-2">
                    <p className="text-zinc-500 text-sm line-through font-mono font-bold">
                      {formatPrice(item.price)}
                    </p>
                    <p className="text-[#c084fc] text-xl font-mono font-black tracking-tight animate-pulse">
                      {formatPrice(finalPrice)}
                    </p>
                    <span className="bg-red-500 text-white text-[8px] font-black px-1.5 py-0.5 uppercase tracking-wider">
                      -{discountPercentage}%
                    </span>
                  </div>
                ) : (
                  <p className="text-[#c084fc] text-xl font-mono font-black tracking-tight whitespace-nowrap">
                    {formatPrice(item.price)}
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Form customization section (Compact & Scrollable if needed) */}
          <div className="flex-1 overflow-y-auto custom-scrollbar pr-1 space-y-2.5 my-1">
            {!item.notForSale ? (
              <div className="space-y-2.5">
                {item.steamBackgroundUrl && (
                  <div className="bg-[#120324]/60 border border-[#9333ea]/15 p-2.5 flex items-center justify-between gap-2">
                    <span className="text-[8px] font-black text-zinc-400 uppercase tracking-wider flex items-center gap-1.5 truncate">
                      <Layout className="w-3 h-3 text-[#9333ea] flex-shrink-0" /> {t.suggestedBg}
                    </span>
                    <a href={item.steamBackgroundUrl} target="_blank" rel="noopener noreferrer" className="text-[#c084fc] hover:text-white text-[8px] font-black uppercase tracking-wider flex items-center gap-1 flex-shrink-0 bg-black/50 px-2 py-1 border border-[#9333ea]/20 hover:border-[#9333ea] transition-all">
                      {t.viewOnMarket} <ExternalLink className="w-2.5 h-2.5" />
                    </a>
                  </div>
                )}

                {/* 2-Column Grid for Custom Name & Discord */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {!item.hideSignature && (
                    <div className="space-y-1">
                      <label className="text-[8px] font-black text-[#c084fc] uppercase tracking-wider flex items-center gap-1">
                        <Type className="w-3 h-3" /> {t.personalize}
                      </label>
                      <input 
                        type="text" 
                        maxLength={25} 
                        placeholder={t.namePlaceholder} 
                        value={customName} 
                        onChange={(e) => setCustomName(e.target.value)} 
                        className="w-full bg-black/80 border border-[#9333ea]/25 px-2.5 py-1.5 text-white placeholder-purple-900/50 outline-none focus:border-[#9333ea] transition-all text-xs font-black artistic-font" 
                      />
                    </div>
                  )}

                  {!item.hideDiscord && (
                    <div className="space-y-1">
                      <label className="text-[8px] font-black text-zinc-400 uppercase tracking-wider flex items-center gap-1">
                        <MessageSquare className="w-3 h-3 text-[#9333ea]" /> {t.discord}
                      </label>
                      <input 
                        type="text" 
                        placeholder={t.discordPlaceholder} 
                        value={discord} 
                        onChange={(e) => setDiscord(e.target.value)} 
                        className="w-full bg-black/80 border border-[#9333ea]/25 px-2.5 py-1.5 text-white placeholder-purple-900/50 outline-none focus:border-[#9333ea] transition-all text-xs" 
                      />
                    </div>
                  )}
                </div>

                {!item.hideSignature && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 items-center bg-[#120324]/40 p-2 border border-[#9333ea]/15">
                    {/* Font Link */}
                    <div className="space-y-1">
                      <label className="text-[8px] font-black text-zinc-400 uppercase tracking-wider flex items-center gap-1">
                        <LinkIcon className="w-3 h-3 text-[#9333ea]" /> {t.dafontLabel}
                      </label>
                      <input 
                        type="text" 
                        placeholder={t.dafontPlaceholder} 
                        value={dafontLink} 
                        onChange={(e) => setDafontLink(e.target.value)} 
                        className="w-full bg-black/80 border border-[#9333ea]/25 px-2.5 py-1.5 text-white placeholder-purple-900/50 outline-none focus:border-[#9333ea] transition-all text-[11px] font-mono" 
                      />
                    </div>

                    {/* Color Swatches */}
                    <div className="space-y-1">
                      <span className="text-[8px] font-black text-zinc-400 uppercase tracking-wider flex items-center gap-1">
                        <Paintbrush className="w-3 h-3 text-[#9333ea]" /> {t.color}
                      </span>
                      <div className="flex flex-wrap gap-1.5 items-center">
                        {presetColors.map(color => (
                          <button 
                            key={color} 
                            type="button"
                            onClick={() => setSigColor(color)} 
                            className={`w-5 h-5 border transition-all ${sigColor === color ? 'border-white scale-110 shadow-[0_0_6px_rgba(255,255,255,0.4)]' : 'border-white/10'}`} 
                            style={{ backgroundColor: color }} 
                          />
                        ))}
                        <div className="relative">
                          <input ref={colorInputRef} type="color" className="absolute inset-0 w-0 h-0 opacity-0 pointer-events-none" value={sigColor} onChange={(e) => setSigColor(e.target.value)} />
                          <button 
                            type="button"
                            onClick={() => colorInputRef.current?.click()} 
                            title={t.customColor} 
                            className={`w-5 h-5 border flex items-center justify-center transition-all ${!presetColors.includes(sigColor) ? 'border-white scale-110 shadow-[0_0_6px_rgba(255,255,255,0.4)]' : 'border-white/20'}`} 
                            style={{ backgroundColor: !presetColors.includes(sigColor) ? sigColor : 'transparent', color: !presetColors.includes(sigColor) ? 'transparent' : '#ffffff' }}
                          >
                            <Pipette className={`w-2.5 h-2.5 ${!presetColors.includes(sigColor) ? 'opacity-0' : 'opacity-100'}`} />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {!item.hideNotes && (
                  <div className="space-y-1">
                    <label className="text-[8px] font-black text-zinc-400 uppercase tracking-wider flex items-center gap-1">
                      <MessageSquare className="w-3 h-3 text-[#9333ea]" /> {t.notesLabel}
                    </label>
                    <textarea 
                      rows={2} 
                      placeholder={t.notesPlaceholder} 
                      value={notes} 
                      onChange={(e) => setNotes(e.target.value)} 
                      className="w-full bg-black/80 border border-[#9333ea]/25 px-2.5 py-1.5 text-white placeholder-purple-900/40 outline-none focus:border-[#9333ea] transition-all text-xs resize-none custom-scrollbar" 
                    />
                  </div>
                )}
              </div>
            ) : (
              <div className="h-full min-h-[140px] flex flex-col items-center justify-center text-center p-6 bg-black/40 border border-white/5 opacity-50">
                <Ban className="w-10 h-10 mb-2 text-zinc-600" />
                <p className="font-black uppercase tracking-[0.25em] text-[9px] text-zinc-400">{t.nfs}</p>
              </div>
            )}
          </div>

          {/* Action section (Always visible at the bottom) */}
          <div className="space-y-2 pt-2.5 border-t border-white/5 flex-shrink-0">
            {!item.notForSale && (
              <>
                {!item.hideBuyButton && (
                  <button 
                    onClick={() => { 
                      onAddToCart(itemWithDiscount, customName, discord, steamUrl, 'Custom', dafontLink, posX, posY, scale, sigColor, notes); 
                      onClose(); 
                    }} 
                    className="w-full bg-white hover:bg-[#9333ea] text-black hover:text-white font-black py-3.5 px-4 flex items-center justify-center gap-3 transition-all shadow-lg active:scale-98 border-b-2 border-[#9333ea] uppercase tracking-widest text-xs group"
                  >
                    <Heart className="w-4 h-4 fill-current group-hover:scale-110 transition-transform" /> {t.buy}
                  </button>
                )}

                {item.stripeUrlBr && (
                  <a 
                    href={item.stripeUrlBr}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full bg-[#9333ea] hover:bg-white text-white hover:text-black font-black py-2.5 px-4 flex items-center justify-center gap-2.5 transition-all shadow-md hover:shadow-lg active:scale-98 border-b-2 border-white/20 uppercase tracking-widest text-[10px] text-center"
                  >
                    <CreditCard className="w-4 h-4" />
                    {language === 'pt' ? 'Pagar com Stripe (BRL / PIX)' : 'Pay with Stripe (BRL / PIX)'}
                  </a>
                )}

                {item.stripeUrl && (
                  <a 
                    href={item.stripeUrl}
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="w-full bg-zinc-900 border border-[#9333ea]/30 hover:border-white text-zinc-300 hover:text-white font-black py-2.5 px-4 flex items-center justify-center gap-2.5 transition-all shadow-md active:scale-98 uppercase tracking-widest text-[10px] text-center"
                  >
                    <CreditCard className="w-4 h-4" />
                    {language === 'pt' ? 'Pagar com Stripe (USD)' : 'Pay with Stripe (USD)'}
                  </a>
                )}
              </>
            )}

            <button
              type="button"
              onClick={handleCopyLink}
              className={`w-full py-2 px-3 flex items-center justify-center gap-2 transition-all font-black uppercase tracking-wider text-[10px] border ${
                copied
                  ? 'bg-emerald-950/90 text-emerald-300 border-emerald-500/50'
                  : 'bg-purple-950/40 hover:bg-[#9333ea]/30 text-purple-300 hover:text-white border-purple-500/30'
              }`}
            >
              {copied ? <Check className="w-3.5 h-3.5 text-emerald-400" /> : <Share2 className="w-3.5 h-3.5" />}
              {copied ? t.linkCopied : (language === 'pt' ? 'Compartilhar Link da Arte' : 'Share Artwork Link')}
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
};

export default ImageModal;
